import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import moment from 'moment';
import _ from 'lodash';

import Utils from '../../../utils/utils';

import Accordion from 'react-bootstrap/Accordion';
import { Button } from 'react-bootstrap';

import Toolbar from '../../controls/Toolbar';
import Icon from '../../controls/Icon';
import { revokeRegistration } from '../../../services/notifications';
import { useNotifications } from '../NotificationsContext';
import FilterHeader from '../FilterHeader';
import DATE_VARIALBES from '../../../config/dateVariables';
import { useHasPermission } from '../../../common/Hooks.jsx';

const UserDetails = ({
    userId,
    registrationsData,
}) => {
    const { id } = useParams();
    const notificationPermissions = useHasPermission({
        domain: Utils.getDomain(),
        environment: null,
        deployment: id,
        type: 'notification',
    });
    const { refreshData } = useNotifications();
    const [translate] = useTranslation();
    const STATUS_FILTER_OPTIONS = useMemo(() => [
        { value: 'read', displayValue: translate("OTM_NOTIFICATIONS_STATUS_READ") },
        { value: 'sent', displayValue: translate("OTM_NOTIFICATIONS_STATUS_SENT") },
    ], [translate]);

    const [filter, setFilter] = useState("90d");
    const [statusFilter, setStatusFilter] = useState([STATUS_FILTER_OPTIONS[0].value, STATUS_FILTER_OPTIONS[1].value]);
    const [minDate] = useState(new Date(Date.now() - DATE_VARIALBES.NINETY_DAYS_IN_MS));
    const [maxDate] = useState(new Date());
    const [startDate, setStartDate] = useState(minDate);
    const [endDate, setEndDate] = useState(maxDate);
    const [filteredData, setFilteredData] = useState(_.cloneDeep(registrationsData));
    const [showRevoked, setShowRevoked] = useState(false);

    useEffect(() => {
        const result = {};

        Object.entries(_.cloneDeep(registrationsData)).forEach(
            ([registrationId, registration]) => {
                let filteredNotifications = registration.notifications.filter(({ createdAt }) =>
                    moment(createdAt) <= moment(endDate) &&
                    moment(createdAt) >= moment(startDate)
                );
                filteredNotifications = filteredNotifications.filter(({ status }) => statusFilter.includes(status));
                registration.notifications = filteredNotifications;

                if (showRevoked || !registration.revoked) {
                    result[registrationId] = registration;
                }
            }
        );

        setFilteredData(result);
    }, [registrationsData, showRevoked, startDate, endDate, statusFilter]);

    const notificationsList = useCallback((notifications) =>
        <div>
            {notifications.length > 0 ? notifications.map(({ _id, createdAt, status, payload }) => (
                <div className="d-flex align-items-center" key={_id}>
                    <Button
                        variant="outline-primary"
                        disabled={true}
                        size="sm"
                        className="border-0"
                    >
                        <Icon icon={status === "sent" ? "CircleFill" : "Circle"} />
                    </Button>
                    <span> {`${payload?.title} | ${Utils.getFormattedDateTime(createdAt)} | ${status}`} </span>
                </div>
            )) :
                <span>{translate("OTM_NO_NOTIFICATIONS")}</span>
            }
        </div>, [translate]);

    const getRegistrationDescription = useCallback((registration) => {
        const { notifications, createdAt } = registration;
        const notificationsUpdatedAt = notifications.map(({ updatedAt }) => moment(updatedAt));
        const lastSeen = notifications.length > 0 ? Utils.getFormattedDateTime(moment.max(notificationsUpdatedAt)) : '';


        let description = `${notifications.length} ${translate("OTM_NOTIFICATIONS")}`;

        if (createdAt) {
            description += ` | ${translate("OTM_NOTIFICATIONS_REGISTERED_AT")}: ${Utils.getFormattedDateTime(createdAt)}`;
        }

        if (lastSeen) {
            description += ` | ${translate("OTM_NOTIFICATIONS_LAST_SEEN")}: ${lastSeen}`;
        }

        return description;
    }, [translate]);

    const renderRegistrationItem = useCallback(([registrationId, registration]) => {
        const { userAgent, notifications, revoked } = registration;

        return (
            <div className="d-flex">
                <div style={{ flex: "auto" }}>
                    <Accordion.Item eventKey={registrationId} key={registrationId}>
                        <Accordion.Header>
                            <div>
                                <span className="d-block">
                                    {revoked && <span className="fw-bold text-danger me-2">[{translate("OTM_NOTIFICATIONS_REVOKED")}]</span>}
                                    {userAgent}
                                </span>
                                <span className="d-block">
                                    {getRegistrationDescription(registration)}
                                </span>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            {notificationsList(notifications)}
                        </Accordion.Body>
                    </Accordion.Item>
                </div>
                <div className="my-auto ms-2">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        disabled={!notificationPermissions.update}
                        className={`${revoked && 'invisible'} border-0`}
                        onClick={async () => {
                            await revokeRegistration({ firebaseRegistrationId: registrationId });
                            refreshData();
                        }}
                    >
                        <Icon icon="DeleteOutlined" />
                    </Button>
                </div>
            </div>
        );
    }, [refreshData, notificationPermissions.update, getRegistrationDescription, notificationsList, translate]);

    return (
        <div className="card h-100">
            <div className="card-header border-bottom">
                <Toolbar
                    heading={userId}
                />
            </div>
            <div className="card-body overflow-hidden h-100">
                <div className="h-100" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
                    <div className="sticky-top bg-white">
                        <FilterHeader
                            filter={filter}
                            setFilter={setFilter}
                            minDate={minDate}
                            maxDate={maxDate}
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            showRevoked={showRevoked}
                            setShowRevoked={setShowRevoked}
                            statusFilterOptions={STATUS_FILTER_OPTIONS}
                            statusFilter={statusFilter}
                            setStatusFilter={setStatusFilter}
                        />
                    </div>
                    <Accordion alwaysOpen>
                        {Object.entries(filteredData).map(renderRegistrationItem)}
                    </Accordion>
                </div>
            </div>
        </div>
    );
};

export default UserDetails;
